import React, { useState } from 'react'
import { Briefcase, CheckCircle, Clock, Users, ArrowRight, Plus } from 'lucide-react'

export default function OnboardingPage() {
  const [onboardingTasks] = useState([
    { id: 1, task: 'Complete IT Setup', category: 'IT', priority: 'High', status: 'Completed', assignee: 'IT Team' },
    { id: 2, task: 'Orientation Meeting', category: 'HR', priority: 'High', status: 'Completed', assignee: 'HR Manager' },
    { id: 3, task: 'Benefits Enrollment', category: 'HR', priority: 'Medium', status: 'In Progress', assignee: 'Benefits Admin' },
    { id: 4, task: 'Department Introduction', category: 'Operations', priority: 'Medium', status: 'Pending', assignee: 'Department Lead' },
    { id: 5, task: 'Training Modules', category: 'Development', priority: 'Medium', status: 'Pending', assignee: 'Training Team' },
    { id: 6, task: 'Compliance Documents', category: 'HR', priority: 'High', status: 'Pending', assignee: 'Legal' },
  ])

  const [newHires] = useState([
    { id: 1, name: 'Emma Wilson', department: 'Engineering', role: 'Junior Developer', startDate: '2026-05-01', progress: 100 },
    { id: 2, name: 'James Brown', department: 'Sales', role: 'Sales Associate', startDate: '2026-05-05', progress: 67 },
    { id: 3, name: 'Lisa Garcia', department: 'Marketing', role: 'Marketing Specialist', startDate: '2026-05-08', progress: 33 },
  ])

  const completedTasks = onboardingTasks.filter((t) => t.status === 'Completed').length
  const totalTasks = onboardingTasks.length

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-500/20 text-green-300'
      case 'In Progress':
        return 'bg-blue-500/20 text-blue-300'
      case 'Pending':
        return 'bg-yellow-500/20 text-yellow-300'
      default:
        return 'bg-slate-500/20 text-slate-300'
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'text-red-400'
      case 'Medium':
        return 'text-yellow-400'
      case 'Low':
        return 'text-green-400'
      default:
        return 'text-slate-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Onboarding & New Hires</h1>
          <p className="text-slate-400">Automated onboarding workflows and employee training management</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6 hover:border-green-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Completed Tasks</h3>
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-3xl font-bold text-white">{completedTasks}</div>
            <div className="text-sm text-slate-400">Out of {totalTasks}</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">New Hires</h3>
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-3xl font-bold text-white">{newHires.length}</div>
            <div className="text-sm text-slate-400">Currently onboarding</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-yellow-500/20 p-6 hover:border-yellow-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">In Progress</h3>
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="text-3xl font-bold text-white">{onboardingTasks.filter((t) => t.status === 'In Progress').length}</div>
            <div className="text-sm text-slate-400">Active tasks</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-cyan-500/20 p-6 hover:border-cyan-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Completion Rate</h3>
              <ArrowRight className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-white">{((completedTasks / totalTasks) * 100).toFixed(0)}%</div>
            <div className="text-sm text-slate-400">Overall progress</div>
          </div>
        </div>

        {/* New Hires Progress */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-400" />
            New Hires Progress
          </h2>

          <div className="space-y-4">
            {newHires.map((hire) => (
              <div key={hire.id} className="bg-slate-700/50 rounded-lg p-4 border border-blue-500/20">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-white font-semibold">{hire.name}</h3>
                    <p className="text-sm text-slate-400">
                      {hire.role} • {hire.department}
                    </p>
                  </div>
                  <span className="text-cyan-400 font-bold">{hire.progress}%</span>
                </div>
                <div className="w-full bg-slate-600 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${hire.progress}%` }}
                  ></div>
                </div>
                <div className="text-xs text-slate-400 mt-2">Started: {hire.startDate}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Onboarding Checklist */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-purple-500/20 p-6">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-purple-400" />
            Onboarding Checklist
          </h2>

          <div className="space-y-3">
            {onboardingTasks.map((task) => (
              <div key={task.id} className="bg-slate-700/50 rounded-lg p-4 border border-purple-500/20 hover:border-purple-400/40 transition-all">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-white font-semibold">{task.task}</h3>
                    <p className="text-sm text-slate-400">Assigned to: {task.assignee}</p>
                  </div>
                  <div className="flex gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(task.status)}`}>
                      {task.status}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold bg-slate-700 ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="px-2 py-1 bg-slate-800 rounded">{task.category}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Training Modules */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-indigo-500/20 p-6">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-indigo-400" />
            Required Training Modules
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'Company Culture & Values', duration: '2h', category: 'HR' },
              { title: 'IT & Systems Overview', duration: '3h', category: 'IT' },
              { title: 'Compliance & Legal', duration: '1h', category: 'HR' },
              { title: 'Role-Specific Training', duration: '8h', category: 'Department' },
              { title: 'Benefits & Health & Safety', duration: '1.5h', category: 'HR' },
              { title: 'Security & Data Protection', duration: '1h', category: 'Security' },
            ].map((module, idx) => (
              <div key={idx} className="bg-slate-700/50 rounded-lg p-4 border border-indigo-500/20 hover:border-indigo-400/40 transition-all">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-white font-semibold flex-1">{module.title}</h3>
                  <span className="text-indigo-400 font-bold text-sm">{module.duration}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-300">{module.category}</span>
                  <button className="text-blue-400 hover:text-blue-300 transition-all text-sm font-semibold">
                    View →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
